// Signed-in identity in the header + full sign-out.
//
// Identity comes from the Entra TOKEN (getCurrentUser), deliberately not from
// the roster: the header must answer "who am I signed in as?" even when the
// API is unreachable or a write failed — exactly when that question matters
// most. Authorship became automatic and invisible with the identity work, so
// on a shared machine this chip is the only thing telling the next person
// they're still in someone else's session.
//
// Sign-out is the FULL Microsoft logout (logoutRedirect), not a local cache
// clear: with single sign-on, a local-only clear means the next click of
// "Sign in" silently resumes the same account — which defeats the shared-
// machine case this exists for. Renders nothing when auth is off.

import * as React from "react";
import { getCurrentUser, signOut } from "@/lib/auth";
import { makeInitials, colorForIndex } from "@/lib/constants";
import { Icon } from "./icons";

// Stable avatar color from the identity itself (no roster index here — the
// chip shouldn't change color as people join the roster).
function colorFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return colorForIndex(Math.abs(h));
}

export function UserMenu() {
  const user = getCurrentUser(); // null when auth is off or nobody signed in
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  const initials = makeInitials(user.name);
  const color = colorFor(user.email ?? user.name);
  const firstName = user.name.split(/\s+/)[0];

  return (
    <div ref={wrapRef} className="user-menu">
      <button
        type="button"
        className="user-chip"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Signed in as ${user.name}`}
        title={user.name}
      >
        <span className="avatar sm" style={{ background: color }}>{initials}</span>
        <span className="user-chip-name">{firstName}</span>
        <Icon name="chevron-down" size={12} />
      </button>

      {open && (
        <div className="popover user-menu-pop" role="menu" aria-label="Account">
          <div className="user-menu-id">
            <span className="avatar" style={{ background: color }}>{initials}</span>
            <div className="user-menu-who">
              <div className="user-menu-name">{user.name}</div>
              {user.email && <div className="user-menu-email">{user.email}</div>}
            </div>
          </div>
          <div className="popover-divider" />
          <div
            className="popover-item"
            role="menuitem"
            tabIndex={0}
            onClick={() => signOut()}
            onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); signOut(); } }}
          >
            Sign out
          </div>
        </div>
      )}
    </div>
  );
}
